import type { ApolloError, ApolloQueryResult, QueryHookOptions } from '@apollo/client';
import { fetchLocalesQuery, fetchLocalesSettings } from 'api/queries/locales';
import { selectLocaleImprovementsEnabled } from 'core/userContext/selectors';
import { DocumentNode } from 'graphql';
import { Locale } from 'modules/domains/template';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGraphql } from 'utils/hooks/useGraphql';

type LegacyLocalesSettings = {
  locales: Locale[];
};

type LocalesSettings = {
  locales: Locale[];
  whatsAppLocales: {
    invalidWhatsAppLocales: Locale[];
    validWhatsAppLocales: Locale[];
  };
  isTemplateDefaultLocaleModifiable: boolean;
};

type NewLocalesSettings = {
  localeSettings: LocalesSettings;
};

type QueryLocalesSettingsResponse = NewLocalesSettings | LegacyLocalesSettings;

export type QueryLocalesSettingsVariables = {
  messageMedium: string;
};

interface UseQueryLocalesSettingsState {
  errorMessage?: ApolloError;
  hasError: boolean;
  isLoading: boolean;
  refetch: (
    variables?: Partial<QueryLocalesSettingsVariables> | undefined,
  ) => Promise<ApolloQueryResult<QueryLocalesSettingsResponse>>;
  locales: Locale[];
  invalidWhatsAppLocales: Locale[];
  validWhatsAppLocales: Locale[];
  isTemplateDefaultLocaleModifiable: boolean;
}

export const useQueryLocalesSettings = (
  options: QueryHookOptions<QueryLocalesSettingsResponse, QueryLocalesSettingsVariables>,
  query?: DocumentNode,
): UseQueryLocalesSettingsState => {
  const isLocaleImprovementsEnabled = useSelector(selectLocaleImprovementsEnabled);

  const queryLocales = useMemo(
    () => query || (isLocaleImprovementsEnabled ? fetchLocalesSettings : fetchLocalesQuery),
    [isLocaleImprovementsEnabled, query],
  );

  const { data, error, errorMessage, isLoading, refetch } = useGraphql<
    QueryLocalesSettingsResponse,
    QueryLocalesSettingsVariables
  >(queryLocales, options);

  const legacyLocales = (data as LegacyLocalesSettings)?.locales || undefined;
  const localeSettings = (data as NewLocalesSettings)?.localeSettings || undefined;
  const whatsAppLocales = localeSettings?.whatsAppLocales;

  return {
    errorMessage,
    hasError: !!error,
    isLoading,
    refetch,
    locales: legacyLocales || localeSettings?.locales || [],
    invalidWhatsAppLocales: whatsAppLocales?.invalidWhatsAppLocales || [],
    validWhatsAppLocales: whatsAppLocales?.validWhatsAppLocales || [],
    isTemplateDefaultLocaleModifiable: localeSettings?.isTemplateDefaultLocaleModifiable || false,
  };
};
